// Lecturas y envíos de la pestaña Mensajes. El historial pagina por cursor y su fin es solo
// `nextCursor === null`: una página puede llegar vacía y aun así haber más.
import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DeliveryChannelExtendedEnum } from '@fiado/type-kit/bin/messagesConnector/enums/DeliveryChannelExtendedEnum';
import {
    COLLECTION_QUOTA_DAYS,
    getCustomerCollectionQuota,
    sendCustomerManualMessage,
    type CustomerManualMessageRequest,
} from '@/api/services/loan-credit';
import { listMessageTemplates } from '@/api/services/loan-config/loan-config.client';
import {
    listCustomerMessages,
    type CustomerMessageHistoryItem,
} from '@/api/services/messages-business/messages-business.client';
import { resendReferralCoupon } from '@/api/services/retail-promotions/referralCoupon.client';
import {
    HISTORY_PAGE_SIZE,
    MAX_EMPTY_PAGE_FOLLOWUPS,
    type MessageMomentCategoryEnum,
} from './customerMessages';

const HISTORY_KEY = 'customer-message-history';

function historyKey(retailCustomerId: string, category: MessageMomentCategoryEnum) {
    return [HISTORY_KEY, retailCustomerId, category] as const;
}

export interface CustomerMessageHistoryState {
    items: CustomerMessageHistoryItem[];
    hasFiadoAccount: boolean;
    hasMore: boolean;
    isLoading: boolean;
    isLoadingMore: boolean;
    error: unknown;
    loadMore: () => void;
    refetch: () => void;
}

export function useCustomerMessageHistory(
    retailCustomerId: string,
    category: MessageMomentCategoryEnum,
    enabled: boolean,
): CustomerMessageHistoryState {
    const query = useInfiniteQuery({
        queryKey: historyKey(retailCustomerId, category),
        queryFn: ({ pageParam }) =>
            listCustomerMessages(retailCustomerId, category, {
                cursor: pageParam,
                limit: HISTORY_PAGE_SIZE,
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextCursor ?? undefined,
        enabled,
        retry: false,
        staleTime: 30_000,
    });

    const pages = query.data?.pages;
    const items = useMemo(() => pages?.flatMap((page) => page.items) ?? [], [pages]);
    const pageCount = pages?.length ?? 0;
    // Sin nada que mostrar todavía, pide sola las páginas vacías que trae el filtro por categoría.
    const shouldFollowEmpty =
        query.hasNextPage &&
        !query.isFetchingNextPage &&
        !query.isError &&
        items.length === 0 &&
        pageCount > 0 &&
        pageCount <= MAX_EMPTY_PAGE_FOLLOWUPS;
    const { fetchNextPage } = query;
    useEffect(() => {
        if (shouldFollowEmpty) void fetchNextPage();
    }, [shouldFollowEmpty, pageCount, fetchNextPage]);

    return {
        items,
        hasFiadoAccount: pages?.[0]?.hasFiadoAccount ?? true,
        hasMore: query.hasNextPage,
        isLoading: enabled && (query.isPending || shouldFollowEmpty),
        isLoadingMore: query.isFetchingNextPage,
        error: query.error,
        loadMore: () => {
            void fetchNextPage();
        },
        refetch: () => {
            void query.refetch();
        },
    };
}

export function useCollectionQuota(retailCustomerId: string, enabled: boolean) {
    return useQuery({
        queryKey: ['customer-collection-quota', retailCustomerId],
        queryFn: () => getCustomerCollectionQuota(retailCustomerId, COLLECTION_QUOTA_DAYS),
        enabled,
        retry: false,
        staleTime: 30_000,
    });
}

/** El texto de la plantilla de un momento en un canal, con sus variables sin llenar. */
export function useTemplatePreview(
    messageType: string | null,
    channel: DeliveryChannelExtendedEnum,
    enabled: boolean,
) {
    return useQuery({
        queryKey: ['customer-message-template-preview', messageType, channel],
        queryFn: async () => {
            const page = await listMessageTemplates({
                messageType: messageType ?? '',
                channelType: channel,
                language: 'es',
                country: 'MX',
            });
            return page.items.find((row) => row.channelType === channel)?.template ?? null;
        },
        enabled: enabled && messageType !== null,
        retry: false,
        staleTime: 5 * 60_000,
    });
}

function useRefreshHistoryOnSuccess(retailCustomerId: string, category: MessageMomentCategoryEnum) {
    const queryClient = useQueryClient();
    return () =>
        queryClient.invalidateQueries({ queryKey: historyKey(retailCustomerId, category) });
}

export function useSendCreditManualMessage(
    retailCustomerId: string,
    category: MessageMomentCategoryEnum,
) {
    const refresh = useRefreshHistoryOnSuccess(retailCustomerId, category);
    return useMutation({
        mutationFn: (body: CustomerManualMessageRequest) =>
            sendCustomerManualMessage(retailCustomerId, body),
        onSuccess: refresh,
    });
}

export function useResendReferralCoupon(
    retailCustomerId: string,
    category: MessageMomentCategoryEnum,
) {
    const refresh = useRefreshHistoryOnSuccess(retailCustomerId, category);
    return useMutation({
        mutationFn: () => resendReferralCoupon(retailCustomerId),
        onSuccess: refresh,
    });
}
